// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Placing 360° panoramas by hand on the locked plan (frontend-ayw.3): the
 * arithmetic behind Flyt (drag a marker, turn its heading) and Tilføj
 * 360°-billede (pick a JPEG, click where it was taken), and the Danish copy
 * for what the server says back (`PATCH` / `POST /panoramas`, ruxd#11).
 *
 * Headings follow ReUseX `core/panorama_pose.hpp`: the yaw is the heading
 * of the panorama's optical forward axis (+z, the equirect's centre column)
 * about world +z — 0 along world +x, counter-clockwise seen from above, in
 * (-180, 180]. An upright pose has optical y (down) = world −z and x = y × z.
 * Poses are row-major 4×4, panorama-optical → world (`viewport/panorama.ts`).
 *
 * Pure: no React, no DOM.
 */

import { ApiRequestError } from '../api/client';
import type { PanoramaInfo, PanoramaUploadQuery } from '../api/types';

/** Camera height above the storey floor a new panorama starts at (a monopod at eye height), metres. */
export const DEFAULT_CAMERA_HEIGHT = 1.6;

/** The server's upload cap (`POST /panoramas`): 64 MiB. */
export const MAX_PANORAMA_BYTES = 64 * 1024 * 1024;

/** The widest equirect the server decodes (`POST /panoramas`): 16384 × 8192. */
export const MAX_PANORAMA_WIDTH = 16384;

const DEG = Math.PI / 180;

/** `deg` wrapped into (-180, 180]. */
export function normalizeYaw(deg: number): number {
  let d = deg % 360;
  if (d <= -180) d += 360;
  else if (d > 180) d -= 360;
  // -0 reads as "-0" in an input.
  return d === 0 ? 0 : d;
}

/**
 * The heading of a row-major pose's optical forward axis about world +z, in
 * degrees — ReUseX `core::panorama_yaw_deg` with `up = +z`: looking straight
 * up or down, the optical up axis (−y) carries the heading instead.
 */
export function yawFromPose(pose: readonly number[]): number {
  // Column 2 of the rotation is optical z (forward) in world.
  let hx = pose[2];
  let hy = pose[6];
  if (Math.hypot(hx, hy) < 1e-9) {
    hx = -pose[1];
    hy = -pose[5];
    if (Math.hypot(hx, hy) < 1e-9) return 0;
  }
  return normalizeYaw(Math.atan2(hy, hx) / DEG);
}

/** Whether a pose array is 16 finite numbers. */
function usablePose(pose: readonly number[] | undefined): pose is number[] {
  return Array.isArray(pose) && pose.length === 16 && pose.every((v) => Number.isFinite(v));
}

/**
 * The heading a panorama is shown at: the server's `yaw_deg` when it sends
 * one, else measured off its own pose, else 0 for one borrowing a frame's
 * position (drawn levelled, facing +x — `LEVELLED_BASIS`), else null.
 */
export function panoramaYaw(pano: PanoramaInfo): number | null {
  if (typeof pano.yaw_deg === 'number' && Number.isFinite(pano.yaw_deg)) return normalizeYaw(pano.yaw_deg);
  if (pano.has_pose && usablePose(pano.pose)) return yawFromPose(pano.pose);
  if (pano.has_frame_pose && usablePose(pano.frame_pose)) return 0;
  return null;
}

/** An upright pose (horizon level) at `position`, facing `yawDeg` — ReUseX `upright_panorama_pose`, row-major. */
export function uprightPose(position: readonly [number, number, number], yawDeg: number): number[] {
  const c = Math.cos(yawDeg * DEG);
  const s = Math.sin(yawDeg * DEG);
  const fwd = [c, s, 0];
  const down = [0, 0, -1];
  // right = down × forward
  const right = [down[1] * fwd[2] - down[2] * fwd[1], down[2] * fwd[0] - down[0] * fwd[2], down[0] * fwd[1] - down[1] * fwd[0]];
  return [
    right[0], down[0], fwd[0], position[0],
    right[1], down[1], fwd[1], position[1],
    right[2], down[2], fwd[2], position[2],
    0, 0, 0, 1,
  ];
}

/** A placement edit: any of the world position components and the absolute heading. */
export interface PlacementNudge {
  x?: number;
  y?: number;
  z?: number;
  yaw_deg?: number;
}

/**
 * `pose` with `nudge` applied — ReUseX `apply_panorama_nudge`: a heading
 * edit turns the rotation about world +z by the difference (pitch and roll
 * kept), then the named position components are set.
 */
export function applyNudge(pose: readonly number[], nudge: PlacementNudge): number[] {
  const m = [...pose];
  if (nudge.yaw_deg !== undefined) {
    const delta = (nudge.yaw_deg - yawFromPose(pose)) * DEG;
    const c = Math.cos(delta);
    const s = Math.sin(delta);
    for (let col = 0; col < 3; col += 1) {
      const x = m[col];
      const y = m[4 + col];
      m[col] = c * x - s * y;
      m[4 + col] = s * x + c * y;
    }
  }
  if (nudge.x !== undefined) m[3] = nudge.x;
  if (nudge.y !== undefined) m[7] = nudge.y;
  if (nudge.z !== undefined) m[11] = nudge.z;
  return m;
}

/**
 * What the server will answer for `PATCH {nudge}`, ahead of it (the
 * optimistic update): the panorama's own pose — or, with none, an upright
 * one at its frame's centre facing 0, as ruxd starts from — nudged, marked
 * manual. Null when there is nothing to start from and the nudge does not
 * name a whole position.
 */
export function optimisticPlacement(pano: PanoramaInfo, nudge: PlacementNudge): PanoramaInfo | null {
  let start: number[] | null = null;
  if (pano.has_pose && usablePose(pano.pose)) start = pano.pose;
  else if (pano.has_frame_pose && usablePose(pano.frame_pose)) {
    start = uprightPose([pano.frame_pose[3], pano.frame_pose[7], pano.frame_pose[11]], 0);
  } else if (nudge.x !== undefined && nudge.y !== undefined && nudge.z !== undefined) {
    start = uprightPose([0, 0, 0], 0);
  }
  if (!start) return null;
  const pose = applyNudge(start, nudge);
  return { ...pano, has_pose: true, pose, pose_source: 'manual', yaw_deg: yawFromPose(pose) };
}

/** Replace the panorama with `next.id` in `list` (or append it). */
export function upsertPanorama(list: readonly PanoramaInfo[], next: PanoramaInfo): PanoramaInfo[] {
  const i = list.findIndex((p) => p.id === next.id);
  if (i < 0) return [...list, next];
  const out = [...list];
  out[i] = next;
  return out;
}

/** The plan's drag ray ∩ the marker's height plane (`viewport/panorama.ts`). */
export { rayToPlaneZ } from '../viewport/panorama';

/** Below this distance (metres) a rotate drag says nothing about direction. */
const MIN_DRAG = 1e-3;

/** The heading from `center` toward `point` (world x/y), degrees; null when they coincide. */
export function yawFromDrag(center: readonly [number, number], point: readonly [number, number]): number | null {
  const dx = point[0] - center[0];
  const dy = point[1] - center[1];
  if (Math.hypot(dx, dy) < MIN_DRAG) return null;
  return normalizeYaw(Math.atan2(dy, dx) / DEG);
}

/** `deg` snapped to the nearest multiple of `step` degrees (15° while Shift is held). */
export function snapYaw(deg: number, step = 15): number {
  return normalizeYaw(Math.round(deg / step) * step);
}

/** A heading for an input: one decimal, Danish comma ("-37,5"). */
export function formatYaw(deg: number): string {
  return normalizeYaw(deg).toLocaleString('da-DK', { maximumFractionDigits: 1, useGrouping: false });
}

/** A number typed in Danish or English ("1,6" or "1.6"); null when it is not one. */
export function parseNumberDa(text: string): number | null {
  const t = text.trim().replace(/−/g, '-').replace(',', '.');
  if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** The default capture height for a storey floor at `floorZ`. */
export function defaultCameraZ(floorZ: number | null): number {
  return (floorZ !== null && Number.isFinite(floorZ) ? floorZ : 0) + DEFAULT_CAMERA_HEIGHT;
}

/** Why an image cannot be a panorama (Danish), or null when it can: an equirect is exactly twice as wide as tall. */
export function equirectProblem(width: number, height: number): string | null {
  if (!(width > 0) || !(height > 0)) return 'Billedet kunne ikke læses.';
  if (width > MAX_PANORAMA_WIDTH || height > MAX_PANORAMA_WIDTH / 2) {
    return `Billedet er ${width} × ${height} px — højst 16384 × 8192 px. Skalér det ned og prøv igen.`;
  }
  if (width !== 2 * height) {
    return `Billedet er ${width} × ${height} px — et 360°-billede (equirektangulært) er præcis dobbelt så bredt som højt.`;
  }
  return null;
}

/** Why a file cannot be uploaded (Danish), or null: a JPEG of at most 64 MiB. */
export function fileProblem(file: { name: string; type: string; size: number }): string | null {
  // `image/pjpeg`: what older Windows browsers call a (progressive) JPEG.
  const jpeg = file.type === 'image/jpeg' || file.type === 'image/pjpeg' || (!file.type && /\.jpe?g$/i.test(file.name));
  if (!jpeg) return 'Vælg et JPEG-billede (.jpg).';
  if (file.size === 0) return 'Filen er tom.';
  if (file.size > MAX_PANORAMA_BYTES) return `Filen er ${formatMb(file.size)} — højst 64 MB.`;
  return null;
}

/** Bytes as "12,4 MB". */
export function formatMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toLocaleString('da-DK', { maximumFractionDigits: 1 })} MB`;
}

/**
 * The name to store an upload under: the picked file's base name, its
 * extension made `.jpg` when it is not .jpg/.jpeg, cut to the server's 255
 * bytes; undefined (the server's default) when nothing usable is left.
 */
export function uploadFilename(name: string): string | undefined {
  const base = name.split(/[\\/]/).pop()?.trim() ?? '';
  if (!base || base.startsWith('.')) return undefined;
  const withExt = /\.jpe?g$/i.test(base) ? base : `${base.replace(/\.[^.]*$/, '')}.jpg`;
  const bytes = new TextEncoder().encode(withExt);
  if (bytes.length <= 255) return withExt;
  // Cut the stem, keep the extension.
  const ext = withExt.match(/\.jpe?g$/i)?.[0] ?? '.jpg';
  let stem = withExt.slice(0, -ext.length);
  while (new TextEncoder().encode(stem + ext).length > 255) stem = stem.slice(0, -1);
  return stem ? stem + ext : undefined;
}

/** A validated `POST /panoramas` query, or the reason it is not one (Danish). */
export function uploadQuery(input: {
  x: number;
  y: number;
  z: number | null;
  yawDeg: number | null;
  filename?: string;
}): { query: PanoramaUploadQuery } | { error: string } {
  if (!Number.isFinite(input.x) || !Number.isFinite(input.y)) return { error: 'Klik i planen for at placere billedet.' };
  if (input.z === null || !Number.isFinite(input.z)) return { error: 'Angiv højden som et tal i meter.' };
  if (input.yawDeg === null || !Number.isFinite(input.yawDeg)) return { error: 'Angiv retningen som et tal i grader.' };
  const query: PanoramaUploadQuery = {
    x: round(input.x, 4),
    y: round(input.y, 4),
    z: round(input.z, 4),
    yaw_deg: round(normalizeYaw(input.yawDeg), 2),
  };
  const filename = input.filename ? uploadFilename(input.filename) : undefined;
  if (filename) query.filename = filename;
  return { query };
}

/** The nudge a finished move or turn sends: only what changed, rounded to the millimetre / centidegree. */
export function moveNudge(
  from: { x: number; y: number; z: number; yaw: number | null },
  to: { x: number; y: number; z: number; yaw: number | null },
): PlacementNudge | null {
  const nudge: PlacementNudge = {};
  if (Math.abs(to.x - from.x) >= 5e-4) nudge.x = round(to.x, 4);
  if (Math.abs(to.y - from.y) >= 5e-4) nudge.y = round(to.y, 4);
  if (Math.abs(to.z - from.z) >= 5e-4) nudge.z = round(to.z, 4);
  if (to.yaw !== null && (from.yaw === null || Math.abs(normalizeYaw(to.yaw - from.yaw)) >= 0.05)) {
    nudge.yaw_deg = round(normalizeYaw(to.yaw), 2);
  }
  return Object.keys(nudge).length > 0 ? nudge : null;
}

function round(v: number, digits: number): number {
  const k = 10 ** digits;
  return Math.round(v * k) / k;
}

export type PlacementOp = 'move' | 'add';

/**
 * What a failed placement means, in Danish. The statuses `PATCH` and
 * `POST /panoramas` single out each get their own sentence; the server's own
 * text (English) is never shown for them. Code-first (`error.code`,
 * ruxd-202): each branch tries the code before the message regex it
 * replaces, so an older server (no `code`) still gets the right line.
 */
export function placementErrorText(error: unknown, op: PlacementOp): string {
  const what = op === 'move' ? 'Placeringen blev ikke gemt' : 'Billedet blev ikke tilføjet';
  if (error instanceof ApiRequestError) {
    const { code, message } = error;
    switch (error.status) {
      case 400:
        if (op === 'move') return `${what}: serveren afviste positionen.`;
        if (code === 'panorama_invalid_filename') return `${what}: filnavnet blev afvist — brug et almindeligt navn, der ender på .jpg.`;
        if (code === 'panorama_invalid_pose') return `${what}: positionen eller retningen blev afvist.`;
        return /filename/i.test(message)
          ? `${what}: filnavnet blev afvist — brug et almindeligt navn, der ender på .jpg.`
          : `${what}: positionen eller retningen blev afvist.`;
      case 401:
      case 403:
        return `${what}: du har ikke rettigheder til at redigere sagen.`;
      case 404:
        return `${what}: panoramaet findes ikke længere i sagen.`;
      case 409:
        if (code === 'panorama_name_taken') return `${what}: der findes allerede et panorama med det filnavn.`;
        if (code === 'case_busy') return `${what}: et trin i pipelinen kører — prøv igen, når det er færdigt.`;
        return /filename|exists|findes/i.test(message)
          ? `${what}: der findes allerede et panorama med det filnavn.`
          : `${what}: et trin i pipelinen kører — prøv igen, når det er færdigt.`;
      case 413:
        return `${what}: filen er større end 64 MB.`;
      case 415:
        return `${what}: serveren tager kun imod JPEG-billeder.`;
      case 422:
        if (code === 'panorama_too_large' || /larger than|16384/i.test(message)) return `${what}: billedet er større end 16384 × 8192 px.`;
        if (/truncated/i.test(message)) return `${what}: filen er ufuldstændig (afkortet JPEG) — eksportér billedet igen.`;
        if (/decoded|not a JPEG/i.test(message)) return `${what}: filen kunne ikke læses som JPEG.`;
        return `${what}: filen er ikke et 360°-billede (JPEG, dobbelt så bredt som højt).`;
      case 503:
        return op === 'add'
          ? `${what}: serveren har travlt med andre billeder lige nu — prøv igen om et øjeblik.`
          : `${what}: projektet var optaget et øjeblik — prøv igen.`;
      default:
        return `${what} (fejl ${error.status}).`;
    }
  }
  if (error instanceof TypeError) return `${what}: kunne ikke forbinde til serveren.`;
  return `${what}.`;
}

/**
 * A JPEG's pixel size from its header (the first SOF segment), without
 * decoding it — what the server checks too ("read from the JPEG header,
 * the image is not decoded"). Null when `bytes` is not a JPEG or the frame
 * header lies beyond them (pass the first megabyte or so: EXIF thumbnails
 * can push it past 64 KB).
 */
export function jpegSize(bytes: Uint8Array): { width: number; height: number } | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let i = 2;
  while (i + 3 < bytes.length) {
    if (bytes[i] !== 0xff) return null;
    const marker = bytes[i + 1];
    if (marker === 0xff) {
      i += 1; // fill byte
      continue;
    }
    // Standalone markers carry no length.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
      i += 2;
      continue;
    }
    if (marker === 0xd9 || marker === 0xda) return null; // end, or scan data before any frame header
    const length = (bytes[i + 2] << 8) | bytes[i + 3];
    const sof = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (sof) {
      if (i + 8 >= bytes.length) return null;
      const height = (bytes[i + 5] << 8) | bytes[i + 6];
      const width = (bytes[i + 7] << 8) | bytes[i + 8];
      return { width, height };
    }
    if (length < 2) return null;
    i += 2 + length;
  }
  return null;
}

// ------------------------------------------------------ Esc, step by step --

/** What the placement tool is doing, as far as Esc is concerned. */
export type PlacingSummary =
  | null
  | { kind: 'select'; id: number | null; moving: boolean }
  | { kind: 'add'; uploading: boolean };

export type EscAction = 'cancel-drag' | 'cancel-upload' | 'cancel-add' | 'stop-move' | 'deselect' | 'close';

/**
 * What Esc undoes while placing, innermost first: a drag in progress (the
 * marker goes back), the upload, the add, Flyt, the pick, then the tool.
 * Null when the tool is not open — Esc is then someone else's.
 */
export function escapeAction(dragging: boolean, placing: PlacingSummary): EscAction | null {
  if (placing === null) return null;
  if (dragging) return 'cancel-drag';
  if (placing.kind === 'add') return placing.uploading ? 'cancel-upload' : 'cancel-add';
  if (placing.moving) return 'stop-move';
  if (placing.id !== null) return 'deselect';
  return 'close';
}

// --------------------------------------------- saves in flight, per marker --

/**
 * The placements this client has sent and not heard back about. Each save
 * of a panorama gets the next sequence number; only the latest one's answer
 * (or failure) is applied — an older answer arriving late would put the
 * marker back where it was two edits ago. `pending` holds the latest
 * optimistic copy, laid over a re-read of the list (`panoramas.changed`
 * fires for our own write too, sometimes before our answer) until the
 * latest save settles. Immutable: every step answers a new tracker.
 */
export interface SaveTracker {
  seq: Readonly<Record<number, number>>;
  inflight: Readonly<Record<number, number>>;
  pending: Readonly<Record<number, PanoramaInfo>>;
}

export const EMPTY_TRACKER: SaveTracker = { seq: {}, inflight: {}, pending: {} };

/** Start a save of panorama `id` showing `optimistic` (null: nothing to show ahead). */
export function beginSave(t: SaveTracker, id: number, optimistic: PanoramaInfo | null): { tracker: SaveTracker; seq: number } {
  const seq = (t.seq[id] ?? 0) + 1;
  const pending = { ...t.pending };
  if (optimistic) pending[id] = optimistic;
  else delete pending[id];
  return {
    tracker: { seq: { ...t.seq, [id]: seq }, inflight: { ...t.inflight, [id]: (t.inflight[id] ?? 0) + 1 }, pending },
    seq,
  };
}

/** Save `seq` of `id` answered or failed: `latest` says whether to apply that. */
export function settleSave(t: SaveTracker, id: number, seq: number): { tracker: SaveTracker; latest: boolean } {
  const latest = t.seq[id] === seq;
  const inflight = { ...t.inflight };
  const left = (inflight[id] ?? 1) - 1;
  if (left > 0) inflight[id] = left;
  else delete inflight[id];
  const pending = { ...t.pending };
  if (latest) delete pending[id];
  return { tracker: { ...t, inflight, pending }, latest };
}

/** Whether a save of `id` is still on its way. */
export function isSaving(t: SaveTracker, id: number): boolean {
  return (t.inflight[id] ?? 0) > 0;
}

/** A list read from the server with the unanswered optimistic placements laid over it. */
export function mergePending(list: readonly PanoramaInfo[], pending: Readonly<Record<number, PanoramaInfo>>): PanoramaInfo[] {
  return list.map((p) => pending[p.id] ?? p);
}
